// Global event bus (TECHNICAL-ARCHITECTURE.md §3). Sim publishes, render and
// DOM overlays subscribe — neither side imports the other.

import type {
  ChickenEntity,
  ChoreId,
  ConditionId,
  GameState,
  StormAlert,
  WeatherState,
} from './types';

export interface GameEvents {
  CHICKEN_HATCHED: { chicken: ChickenEntity };
  EGG_LAID: { chickenId: string; tile: { x: number; y: number } };
  DOOR_UNLATCHED: { structureId: string };
  NEIGHBOR_COMPLAINT_TRIGGERED: { kind: 'odor' | 'noise'; severity: number };
  DEV_STATS_OVERRIDE: { path: string; value: unknown };

  /** One completed simulation tick. */
  TICK: { tickCount: number; deltaSimSeconds: number };
  /**
   * One background-tab catch-up planned on return (TECHNICAL-ARCHITECTURE.md
   * §2.3 "While you were away…" summary). Exactly one per genuine hide→show
   * transition that owed time; simulatedTicks are queued for chunked replay,
   * discardedTicks exceeded the cap and were dropped.
   */
  CATCHUP_COMPLETE: {
    hiddenMs: number;
    simulatedTicks: number;
    discardedTicks: number;
    capped: boolean;
  };
  /** State replaced wholesale (load, import, dev override) — resync everything. */
  STATE_LOADED: { state: GameState };
  STATE_SAVED: { timestamp: number };
  SIM_SPEED_CHANGED: { multiplier: number };
  ASSET_FALLBACK: { assetPath: string; reason: string };

  WEATHER_CHANGED: { weather: WeatherState };
  STORM_WATCH: { alert: StormAlert; weather: WeatherState };
  STORM_HIT: { isDrill: boolean };
  STORM_PASSED: { score: number; isDrill: boolean };

  CHICKEN_SICK: { chickenId: string; condition: ConditionId };
  CHICKEN_RECOVERED: { chickenId: string; condition: ConditionId };
  CHICKEN_ESCAPED: { chickenId: string };
  CHORE_COMPLETED: { chore: ChoreId };
  CHECKLIST_COMPLETE: { day: string; reward: number };
  BADGE_EARNED: { badge: string };
  FACT_UNLOCKED: { factId: string };
  ITEM_UNLOCKED: { itemId: string; gate: string };
  EGG_COLLECTED: { eggId: string; eggits: number };
  PREDATOR_ATTACK: { chickenId: string; predator: string };
  /** Fired the first time a player crosses the badge threshold that unlocks
   *  Veteran mode. Payload is the badge count at unlock time. */
  VETERAN_UNLOCKED: { badges: number };
}

export type GameEventName = keyof GameEvents;
type Handler<K extends GameEventName> = (payload: GameEvents[K]) => void;

export class EventBus {
  private handlers = new Map<GameEventName, Set<(payload: never) => void>>();

  /** Returns an unsubscribe function. */
  on<K extends GameEventName>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as (payload: never) => void);
    return () => this.off(event, handler);
  }

  once<K extends GameEventName>(event: K, handler: Handler<K>): () => void {
    const off = this.on(event, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  off<K extends GameEventName>(event: K, handler: Handler<K>): void {
    this.handlers.get(event)?.delete(handler as (payload: never) => void);
  }

  emit<K extends GameEventName>(event: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    // Copy first: handlers may unsubscribe themselves while we iterate.
    for (const handler of [...set]) {
      try {
        (handler as Handler<K>)(payload);
      } catch (err) {
        console.error(`[EventBus] handler for ${event} threw`, err);
      }
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}

export const bus = new EventBus();
